/**
 * Chat mode routing — ChatMode type, mode/tool mapping, mode→sections
 * registry, and tool-suppression configuration.
 *
 * The mode value is derived from the current route pathname in
 * WorkspaceProvider (chatModeFromPathname) and threaded through the chat
 * stack to control which system prompt is assembled, which tools are
 * exposed, and which ToolId owns canvas writes.
 */

import { CANVAS_META, type ToolId } from './canvas-meta'
import { concierge } from './modes/concierge'
import { norma } from './modes/norma'
import { val } from './modes/val'
import { testcases } from './modes/testcases'
import { marq } from './modes/marq'
import { compli } from './modes/compli'
import {
  assembleSystemPrompt,
  type PromptAudience,
  type PromptSection,
} from './identity'
import { chatModeFromPathname as resolveWorkspaceMode } from '@/lib/workspaces/registry'
import { hasCapability } from '@/lib/rbac/has-capability'
import {
  toolRequiredCapabilities,
  CAP_MAPPED_TOOL_NAMES,
} from '@/lib/rbac/tool-capabilities'

// ─── Types ──────────────────────────────────────────────────────────────────

export type ChatMode =
  | 'concierge'
  | 'study-design'
  | 'norma'
  | 'val'
  | 'testcases'
  | 'marq'
  | 'compli'

export { definePersona, type PersonaPromptModule } from './persona'

/**
 * Map the active ChatMode to the ToolId that owns canvas writes from that
 * mode. 1:1 today — every persona has its own canvas. This function is the
 * seam future modes extend.
 */
export function targetToolForMode(mode: ChatMode): ToolId {
  switch (mode) {
    case 'concierge':    return 'concierge'
    case 'study-design': return 'study-design'
    case 'norma':        return 'norma'
    case 'val':          return 'val'
    case 'testcases':    return 'testcases'
    case 'marq':         return 'marq'
    case 'compli':       return 'compli'
    default:             return 'concierge'
  }
}

/**
 * Format a "View in X Canvas" markdown link for a given mode + sectionId.
 * Used by the chat post-processing rewrites that summarize messages with
 * a canvas reference. Honors persona — Norma writes get "View in Codebook
 * Canvas" pointing at /tools/codebooks/canvas, not a generic study-design
 * label.
 */
export function canvasReferenceMarkdown(mode: ChatMode, sectionId: string): string {
  return canvasLinkForTool(targetToolForMode(mode), sectionId)
}

/**
 * Link to a canvas by its OWNER, rather than by whichever persona is being
 * viewed from.
 *
 * The mode-based variant above is only correct at write time, when the active
 * persona is the one receiving the content. On a later read — and every
 * route_handoff produces one, since it moves you to another persona while
 * keeping the session — it renames the link after the viewer. ToolCanvas
 * filters tabs by targetTool, so the resulting link points at a canvas that
 * has been filtered clean of the content it promises.
 */
export function canvasLinkForTool(tool: ToolId, sectionId: string): string {
  const meta = CANVAS_META[tool]
  return `[View in ${meta.name}](${meta.href}#section-${sectionId})`
}

// ─── Mode configuration ─────────────────────────────────────────────────────

interface ModeConfig {
  sections: readonly PromptSection[]
  suppressedTools: Set<string>
}

// HubSpot CRM tools — exposed only in Marq mode (BD discovery workspace).
// Suppressed everywhere else so the model in concierge/study-design doesn't
// see them in the tool catalog at all.
const HUBSPOT_TOOL_NAMES = [
  'hubspot_list_pipelines',
  'hubspot_list_companies', 'hubspot_search_companies', 'hubspot_get_company',
  'hubspot_list_contacts',  'hubspot_search_contacts',  'hubspot_get_contact',
  'hubspot_list_deals',     'hubspot_search_deals',     'hubspot_get_deal',
] as const

// EDC (Nora Rollment) tools surface real clinical study data and are
// execution-gated to STUDY_EDC_TOOL_ALLOWLIST. They are reserved for the future
// Nora enrollment persona, which has no chat mode yet — so EVERY current mode
// suppresses them, val included (their surface:val capability gate predates
// that decision and stays as defense-in-depth, alongside the execution gate).
// When the Nora persona ships, expose them in its mode only.
const EDC_TOOL_NAMES = [
  'edc_list_studies',   'edc_study_overview', 'edc_subject_status',
  'edc_site_enrollment', 'edc_form_completion', 'edc_task_states',
  'edc_site_scorecard',
] as const

// Study-build tools — the ODM study shell and the parallel-build plan /
// validation internals. Study Designer only; every other mode suppresses them.
// All six are also capability-gated on surface:study-design (tool-capabilities.ts).
const STUDY_BUILD_TOOL_NAMES = [
  'generate_study_shell',
  'present_build_plan',
  'override_build_plan',
  'parse_crf_spec',
  'validate_3ph_odm',
  'get_active_process_status',
] as const

// Norma-only P1/P2 codebook domain tools. Concierge/Study Design keep P0
// list/get/search; Val/TestCases keep get/search. Suppress deep tools elsewhere.
const CODEBOOK_NORMA_DEEP_TOOLS = [
  'codebook_form_get',
  'codebook_diff',
  'codebook_structure_get',
  'codebook_field_search',
  'codebook_field_get',
  'codebook_inventory',
  'codebook_rules_scan',
  'codebook_integrity_check',
] as const

const CODEBOOK_ALL_TOOLS = [
  'codebook_list',
  'codebook_get',
  'codebook_search',
  ...CODEBOOK_NORMA_DEEP_TOOLS,
] as const

const MODE_CONFIGS: Record<ChatMode, ModeConfig> = {
  'concierge': {
    sections: concierge.SECTIONS,
    suppressedTools: new Set([
      'update_odm_xml',
      'show_form_preview',
      'compare_odm',
      'update_study_config',
      'display_study',
      ...HUBSPOT_TOOL_NAMES,
      ...EDC_TOOL_NAMES,
      ...CODEBOOK_NORMA_DEEP_TOOLS,
      ...STUDY_BUILD_TOOL_NAMES,
    ]),
  },
  'study-design': {
    // Empty sentinel — caller falls through to buildSystemPrompt() in
    // modes/study-design.ts. The Study Designer prompt is the legacy
    // CRUCIBLE prompt and is treated as the default.
    sections: [],
    suppressedTools: new Set([
      ...HUBSPOT_TOOL_NAMES,
      ...CODEBOOK_NORMA_DEEP_TOOLS,
      ...EDC_TOOL_NAMES,
    ]),
  },
  'marq': {
    sections: marq.SECTIONS,
    suppressedTools: new Set([
      // Study-design XML/form tools — Marq writes BD reports, not full studies.
      // add_to_canvas IS available (writes to Marq's BD Canvas).
      'update_odm_xml',
      'show_form_preview',
      'compare_odm',
      'update_study_config',
      'display_study',
      'get_form',
      'get_item',
      'get_itemgroup',
      'get_codelist',
      // Codebook tools — Norma's domain.
      ...CODEBOOK_ALL_TOOLS,
      ...STUDY_BUILD_TOOL_NAMES,
      ...EDC_TOOL_NAMES,
    ]),
  },
  'norma': {
    sections: norma.SECTIONS,
    suppressedTools: new Set([
      // Study-design XML/canvas tools — Norma writes codebooks, not full studies.
      'update_odm_xml',
      'show_form_preview',
      'compare_odm',
      'update_study_config',
      'display_study',
      'get_form',
      'get_item',
      'get_itemgroup',
      'get_codelist',
      // Marq's CRM surface.
      ...HUBSPOT_TOOL_NAMES,
      ...STUDY_BUILD_TOOL_NAMES,
      ...EDC_TOOL_NAMES,
    ]),
  },
  // Val and TestCases are intentionally given the SAME tool suppression set —
  // they're sister QA personas (Val diagnoses defects after the fact;
  // TestCases specifies tests before the fact). Same tools, different prompts,
  // different canvases. If the suppression sets ever drift, the prompts.test.ts
  // assertion that the two sets are equal will fail — that's the signal to
  // decide whether the drift is intentional (then update the test) or a bug.
  'val': {
    sections: val.SECTIONS,
    suppressedTools: new Set([
      // Val reads codebooks + ODMs to validate them, doesn't author them.
      'update_odm_xml',
      'show_form_preview',
      'compare_odm',
      'update_study_config',
      'display_study',
      // Norma-only library browse + domain; Val keeps codebook_get + codebook_search.
      'codebook_list',
      ...CODEBOOK_NORMA_DEEP_TOOLS,
      // Marq's CRM surface.
      ...HUBSPOT_TOOL_NAMES,
      ...STUDY_BUILD_TOOL_NAMES,
      // Reserved for the future Nora persona — see EDC_TOOL_NAMES.
      ...EDC_TOOL_NAMES,
    ]),
  },
  'testcases': {
    sections: testcases.SECTIONS,
    suppressedTools: new Set([
      // Mirrors Val (see comment above 'val' block).
      'update_odm_xml',
      'show_form_preview',
      'compare_odm',
      'update_study_config',
      'display_study',
      'codebook_list',
      ...CODEBOOK_NORMA_DEEP_TOOLS,
      ...HUBSPOT_TOOL_NAMES,
      ...STUDY_BUILD_TOOL_NAMES,
      ...EDC_TOOL_NAMES,
    ]),
  },
  'compli': {
    sections: compli.SECTIONS,
    suppressedTools: new Set([
      // Compli reads/reasons over Qualio + its own assessment results; it
      // doesn't author studies, codebooks, or CRM records.
      'update_odm_xml',
      'show_form_preview',
      'compare_odm',
      'update_study_config',
      'display_study',
      'get_form',
      'get_item',
      'get_itemgroup',
      'get_codelist',
      ...CODEBOOK_ALL_TOOLS,
      ...HUBSPOT_TOOL_NAMES,
      ...EDC_TOOL_NAMES,
      ...STUDY_BUILD_TOOL_NAMES,
    ]),
  },
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Build the system prompt for a given mode and audience: the shared
 * <crucible>, <organization> and <crucible_guide> layers, then the persona's
 * sections the audience's capabilities unlock. For study-design, returns null
 * (caller should use buildSystemPrompt instead, which assembles the same
 * shared layers ahead of its own sections).
 */
export function buildModeSystemPrompt(
  mode: ChatMode,
  currentDate: string,
  audience: PromptAudience,
): string | null {
  const config = MODE_CONFIGS[mode]
  if (config.sections.length === 0) return null
  return assembleSystemPrompt(config.sections, currentDate, audience)
}

/**
 * Get the set of tool names to suppress for a mode.
 */
export function getModeSuppressedTools(mode: ChatMode): Set<string> {
  // route_handoff is intentionally available in EVERY chat mode — handoffs are
  // any-persona → any-ready-target, INCLUDING back to concierge (the return
  // path that AB#19236 is about). handoffTargets + the card's on-route
  // self-suppression gate which destinations appear, not who may initiate.
  return MODE_CONFIGS[mode].suppressedTools
}

/**
 * Capability-filtered tool suppression (UX layer).
 *
 * Returns the set of tool names to remove from the model's toolset for `mode`
 * given the user's held capabilities `caps`. This is the UNION of:
 *   1. the mode's static `suppressedTools` (persona scoping — always applied), and
 *   2. every cap-mapped tool whose required (surface) capability the user lacks.
 *
 * A tool is cap-suppressed iff it declares required capability/ies AND the
 * user holds none of them (any-of). Default-allow tools (null) are NEVER
 * cap-suppressed.
 *
 * Mirrors the backend tool→surface convention
 * (`get_tool_capability_requirements`). This is UX only; the backend
 * tool-dispatch guard (Task 8) is authoritative.
 *
 * `candidateToolNames` lets the live call site (chat-v3) pass the actual built
 * tool names so prefix-mapped families (hubspot_*, qualio_*, …) beyond the
 * statically-listed names are also evaluated. When omitted, the mode's static
 * set plus the exact-mapped tool names are evaluated — sufficient for the
 * concrete names callers care about, since the static set already enumerates
 * the prefix-family members it suppresses.
 */
export function effectiveSuppressedTools(
  mode: ChatMode,
  caps: string[],
  candidateToolNames?: Iterable<string>,
): Set<string> {
  const staticSet = MODE_CONFIGS[mode].suppressedTools
  const result = new Set<string>(staticSet)

  // Build the universe of tool names to evaluate for cap-suppression.
  const candidates = new Set<string>(CAP_MAPPED_TOOL_NAMES)
  for (const name of staticSet) candidates.add(name)
  if (candidateToolNames) {
    for (const name of candidateToolNames) candidates.add(name)
  }

  for (const tool of candidates) {
    const required = toolRequiredCapabilities(tool)
    if (required !== null && !required.some((r) => hasCapability(caps, r))) {
      result.add(tool)
    }
  }

  return result
}

/**
 * Derive ChatMode from a Next.js pathname.
 *
 * /tools/study-design is the canonical route; /study-design and /workspace
 * are legacy redirects that arrive here via Next's `redirect()` already
 * resolved, so we don't match them.
 *
 * Each tool has its own mode. The default fallthrough is 'concierge' —
 * safer than 'study-design' for unrecognized routes since Concierge has
 * a tighter tool surface and a general-purpose persona.
 *
 * SAFETY CHAIN — when this returns the wrong/unknown mode:
 *   1. chat-v3/route.ts coerces `body.mode || 'concierge'` for empty input
 *   2. buildModeSystemPrompt(unknown-mode) returns null → caller falls through
 *      to the legacy buildSystemPrompt() (Study Designer's bespoke prompt)
 *   3. The PersonaPromptModule type stops the build if a new mode is added
 *      without its sections being wired up
 * So this resolver is best-effort routing; the chain catches everything it
 * gets wrong without crashing the chat stack.
 */
export function chatModeFromPathname(pathname: string | null): ChatMode {
  // Delegates to the workspace registry — the single source of truth for the
  // route→persona map (docs/internal/workspace-registry-design.md). Always
  // returns a chat-capable id (non-chat workspaces fall back to concierge),
  // so the WorkspaceId→ChatMode narrowing is sound.
  return resolveWorkspaceMode(pathname) as ChatMode
}
